import React, { useState } from 'react'
import { dark, radius, status as statusColors, type as typo } from '../tokens'

/**
 * Everything that distinguishes the two verdicts, held here rather than passed
 * in. The grid card and the preview modal both render this pair, and a caller
 * that chose its own colour or wording is exactly how the card came to show a
 * solid green Approve while the modal used the same fill to mean "approved".
 */
const VERDICT = {
  approve: {
    label: 'Approve',
    recordedLabel: 'Approved',
    tone: statusColors.success,
    // The tone at low alpha, for the recorded state's ring.
    ring: 'rgba(34, 197, 94, 0.22)',
  },
  reject: {
    label: 'Reject',
    recordedLabel: 'Rejected',
    tone: statusColors.danger,
    ring: 'rgba(253, 57, 122, 0.22)',
  },
} as const

export type Verdict = keyof typeof VERDICT

/**
 * One verdict, as a toggle.
 *
 * Filled means recorded, and only recorded: an unpressed verdict is its colour on
 * an outline. Saturation reads as "on" whatever the label says, so a solid green
 * Approve looked more approved than an actual approval did — and a filled default
 * on the happy path is a nudge the reviewer didn't ask for. Both verdicts carry
 * the same weight until one of them is chosen.
 *
 * Pressing a recorded verdict again is how it is undone or revised, which is what
 * replaced a separate "Remove approval" button — a permanent slot spent on the
 * rarest act in the dialog. What a pressed state can't say is that it is
 * pressable, so hovering turns the check into a clear-it cross and names the act
 * in the tooltip. The label itself holds still: swapping "Approved" for "Remove
 * approval" resizes the button and shoves the verdict beside it sideways, under
 * the pointer that is arriving.
 */
export function VerdictButton({
  verdict,
  undo,
  wide,
  block,
  recorded,
  onClick,
  disabled,
  dimmed,
}: {
  verdict: Verdict
  /**
   * What a second press does, named and iconed. Absent when the viewer can't undo
   * this verdict — the button is then a state and nothing more.
   */
  undo?: { label: string; icon: string }
  /** Roomier, for the verdict the reviewer reaches for most. */
  wide?: boolean
  /** Split the row evenly, for the pair sitting under a card in the grid. */
  block?: boolean
  recorded: boolean
  onClick?: () => void
  disabled: boolean
  dimmed: boolean
}) {
  const [hover, setHover] = useState(false)
  const activeHover = hover && !disabled
  const { label, recordedLabel, tone, ring } = VERDICT[verdict]

  return (
    <button
      type="button"
      aria-label={label}
      aria-pressed={recorded}
      title={recorded && undo ? undo.label : undefined}
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      disabled={disabled}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 7,
        flex: block ? 1 : '0 0 auto',
        padding: wide ? '8px 24px' : '8px 16px',
        borderRadius: radius.sm,
        border: `1px solid ${tone}`,
        fontFamily: typo.family.sans,
        fontSize: typo.size.body,
        fontWeight: typo.weight.semibold,
        ...(recorded
          ? // Dark ink on the fill: white over #22c55e is barely 2.3:1, and it is
            // what makes the check read as a mark rather than a smudge.
            {
              background: tone,
              color: dark.accentInk,
              boxShadow: `0 0 0 ${activeHover ? 5 : 3}px ${ring}`,
            }
          : { background: activeHover ? ring : 'transparent', color: tone }),
        transition: 'background 0.15s ease, box-shadow 0.15s ease',
        opacity: dimmed ? 0.6 : 1,
        cursor: disabled ? 'default' : 'pointer',
      }}
    >
      {/* `fa-fw` because the glyphs are proportional: swapping the check for a
        cross on hover otherwise resizes the button under the pointer, which is
        the jitter the steady label was there to avoid in the first place. */}
      {recorded && (
        <i
          className={`fa fa-fw fa-${activeHover && undo ? undo.icon : 'check'}`}
          aria-hidden="true"
        />
      )}
      {recorded ? recordedLabel : label}
    </button>
  )
}
